import { createElement, ReactElement, ReactNode } from 'react';
import { Annotation } from '../../../Annotation/types';
import Highlight from '../../../Highlighting/components/Highlight';
import { getNonWhitespaceCount } from '../../../Highlighting/utils/highlightPositionUtils';
import {
  BlockAnnotationsLookup,
  BlockIndex,
  Child,
  Children,
  MarkEl,
} from './types';
import {
  extractText,
  isElementChild,
  isMarkNode,
  toChildrenArray,
} from './guards';

/** Check if the annotation starts and ends inside the given block. */
function blockContainsEntireAnnotation(
  blockId: BlockIndex,
  annotation: Annotation,
) {
  const { start, end } = annotation.position;
  return start.blockId === blockId && end.blockId === blockId;
}

function getAnnotationForId(annotations: Annotation[], annotationId?: string) {
  if (!annotations || annotations.length === 0) {
    return undefined;
  }
  if (annotationId) {
    return annotations.find((a) => a.id === annotationId);
  }
  if (annotations.length === 1) {
    return annotations[0];
  }
  return undefined;
}

function targetNonWhitespaceFromOffsets(
  annotation: Annotation | undefined,
): number {
  if (!annotation) {
    return 0;
  }
  const start = annotation.position.start?.offset ?? 0;
  const end = annotation.position.end?.offset ?? start;
  return Math.max(0, end - start);
}

type SplitChildrenResult = {
  keep: Children;
  remainder: Children;
};

function shouldConsumeWholePart(
  accumulatedNonWs: number,
  partNonWs: number,
  targetNonWs: number,
): boolean {
  return accumulatedNonWs + partNonWs < targetNonWs;
}

function computeSplitIndexForNeed(txt: string, need: number): number {
  let splitIndex = txt.length; // By default the split index is the end of the text, so string is not split.
  const nonWsCharacterRegex = /\S/g;
  let match: RegExpExecArray | null;
  let nonWsCharacterCount = 0;
  while ((match = nonWsCharacterRegex.exec(txt)) !== null) {
    nonWsCharacterCount++;
    if (nonWsCharacterCount === need) {
      splitIndex = match.index + 1;
      break;
    }
  }
  return splitIndex;
}

function appendSplitResultToBuckets(
  part: Child,
  idx: number,
  blockId: number | undefined,
  highlightedText: string,
  remainingText: string,
  highlightedNodes: Children,
  remainderNodes: Children,
): void {
  if (typeof part === 'string') {
    highlightedNodes.push(highlightedText);

    if (remainingText) {
      remainderNodes.push(remainingText);
    }
    return;
  }

  /* The original part is a React element. Create new elements for both highlighted
   and unhighlighted part of the element, using the same element type and
   props to preserve the original structure as much as possible.*/
  const elementType = (part as ReactElement).type || 'span';
  const elementProps = ((part as ReactElement).props || {}) as Record<
    string,
    unknown
  >;
  highlightedNodes.push(
    createElement(
      elementType,
      { ...elementProps, key: `trim-${blockId ?? 'b'}-${idx}` },
      highlightedText,
    ),
  );

  if (remainingText) {
    remainderNodes.push(
      createElement(
        elementType,
        { ...elementProps, key: `trim-${blockId ?? 'b'}-${idx}` },
        remainingText,
      ),
    );
  }
}

interface SplitLoopState {
  accumulatedTotalNonWsCount: number;
  splitOccurred: boolean;
}

function processSplitIteration(
  part: Child,
  idx: number,
  blockId: number | undefined,
  targetNonWs: number,
  state: SplitLoopState,
  highlightedNodes: Children,
  remainderNodes: Children,
): SplitLoopState {
  const txt = extractText(part) || '';
  const nonWs = getNonWhitespaceCount(txt);

  if (state.splitOccurred) {
    /* No need to find split index anymore. Add the entire part to the
    remainder nodes (= unhighlighted parts) and continue to next part. */
    remainderNodes.push(part);
    return { ...state };
  }

  if (
    shouldConsumeWholePart(state.accumulatedTotalNonWsCount, nonWs, targetNonWs)
  ) {
    /* The entire part is inside the highlight.
    Add it to the highlighted nodes and continue to next part. */
    highlightedNodes.push(part);
    return {
      accumulatedTotalNonWsCount: state.accumulatedTotalNonWsCount + nonWs,
      splitOccurred: false,
    };
  }

  /** The count of remaining non-whitespace characters in the highlight that are unaccounted for.*/
  const need = targetNonWs - state.accumulatedTotalNonWsCount;
  if (need <= 0) {
    /* There are no more non-whitespace characters to add to the highlight
    (= split has occurred in an earlier part). Add the entire part to the
    remainder nodes and continue to next part. */
    remainderNodes.push(part);
    return { ...state, splitOccurred: true };
  }

  // Find split index for
  const splitIndex = computeSplitIndexForNeed(txt, need);

  // Split the text into highlighted and remaining parts.
  const highlightedText = txt.slice(0, splitIndex);
  const remainingText = txt.slice(splitIndex);

  appendSplitResultToBuckets(
    part,
    idx,
    blockId ?? undefined,
    highlightedText,
    remainingText,
    highlightedNodes,
    remainderNodes,
  );

  return {
    accumulatedTotalNonWsCount:
      state.accumulatedTotalNonWsCount + getNonWhitespaceCount(highlightedText),
    splitOccurred: true,
  };
}

/**
 * Find the end position of the highlighted part in the children, and split the
 * children into highlighted and remainder (unhighlighted) parts.
 */
function splitChildrenAtNonWhitespaceTarget(
  parts: Children,
  targetNonWs: number,
  blockId?: number,
): SplitChildrenResult {
  let state: SplitLoopState = {
    accumulatedTotalNonWsCount: 0, // Collects the total non-whitespace count of the parts that have been iterated over.
    splitOccurred: false,
  };
  const highlightedNodes: Children = []; // Highlighted parts
  const remainderNodes: Children = []; // Unhighlighted parts

  for (let idx = 0; idx < parts.length; idx++) {
    const part: Child = parts[idx];
    state = processSplitIteration(
      part,
      idx,
      blockId ?? undefined,
      targetNonWs,
      state,
      highlightedNodes,
      remainderNodes,
    );
  }

  return { keep: highlightedNodes, remainder: remainderNodes };
}

function splitChildren(
  parts: Children,
  annotation: Annotation,
  blockAnnotations: Annotation[],
  blockId: BlockIndex | undefined,
): SplitChildrenResult {
  const annotationInBlock = getAnnotationForId(blockAnnotations, annotation.id);
  const nonWsTarget = targetNonWhitespaceFromOffsets(annotationInBlock);
  return splitChildrenAtNonWhitespaceTarget(
    parts,
    nonWsTarget,
    blockId ?? undefined,
  );
}

/**
 * Scan forward from firstMark to merge sequences of mark → element → mark for
 * the same annotation, preserving leading/trailing whitespace around the element.
 * If the next pattern isn’t found or annotation IDs differ, it stops and hands
 * control back to the outer loop.
 */
function collectWhitespaceForward(
  originalChildren: ReactNode[],
  startIndex: number,
): { whitespace: Children; nextIndex: number } {
  const whitespace: Children = [];
  let index = startIndex;
  while (
    index < originalChildren.length &&
    typeof originalChildren[index] === 'string' &&
    (originalChildren[index] as string).trim() === ''
  ) {
    whitespace.push(originalChildren[index] as string);
    index++;
  }
  return { whitespace, nextIndex: index };
}

function isIndexElementNode(children: ReactNode[], index: number): boolean {
  return index < children.length && isElementChild(children[index]);
}

function isIndexMarkNode(children: ReactNode[], index: number): boolean {
  return index < children.length && isMarkNode(children[index]);
}

function areAnnotationsCompatible(
  current: Annotation,
  candidateMark: MarkEl,
  blockAnnotations: Annotation[],
): boolean {
  const nextAnnotationCandidate = getAnnotationForId(
    blockAnnotations,
    candidateMark.props.annotation?.id,
  );
  return (
    !!current &&
    !!nextAnnotationCandidate &&
    nextAnnotationCandidate.id === current.id
  );
}

function appendSequenceParts(
  mergedParts: Children,
  leadingWhitespace: Children,
  elementNode: ReactElement,
  trailingWhitespace: Children,
  nextMark: MarkEl,
) {
  mergedParts.push(...leadingWhitespace);
  mergedParts.push(elementNode);
  mergedParts.push(...trailingWhitespace);
  /* Push children of the subsequent <mark> so text continues seamlessly. */
  mergedParts.push(...toChildrenArray(nextMark));
}

interface ScanState {
  scanIndex: number;
  mergedPairs: number;
  resumeIndex: number;
  stoppedOnUnpairedElement: boolean;
}

function processScanMarkIteration(
  originalChildren: ReactNode[],
  state: ScanState,
  blockAnnotations: Annotation[],
  annotation: Annotation,
  mergedParts: Children,
): { state: ScanState; shouldBreak: boolean } {
  // Collect leading whitespace before the element.
  const { whitespace: leadingWhitespace, nextIndex: afterLeading } =
    collectWhitespaceForward(originalChildren, state.scanIndex);

  // If we ran out or the next node is not an element, stop scanning.
  if (!isIndexElementNode(originalChildren, afterLeading)) {
    return { state: { ...state, scanIndex: afterLeading }, shouldBreak: true };
  }

  const elementIndex = afterLeading;
  const elementNode = originalChildren[elementIndex] as ReactElement;

  // Move past the element and collect trailing whitespace.
  const { whitespace: trailingWhitespace, nextIndex: afterTrailing } =
    collectWhitespaceForward(originalChildren, elementIndex + 1);

  // If we ran out or the next node is not a mark, this element is unpaired.
  if (!isIndexMarkNode(originalChildren, afterTrailing)) {
    return {
      state: {
        ...state,
        resumeIndex: elementIndex,
        stoppedOnUnpairedElement: true,
        scanIndex: afterTrailing,
      },
      shouldBreak: true,
    };
  }

  const nextMark = originalChildren[afterTrailing] as MarkEl;
  const compatible = areAnnotationsCompatible(
    annotation,
    nextMark,
    blockAnnotations,
  );

  // If annotation IDs differ, we also stop and hand control back.
  if (!compatible) {
    return {
      state: {
        ...state,
        resumeIndex: elementIndex,
        stoppedOnUnpairedElement: true,
        scanIndex: afterTrailing,
      },
      shouldBreak: true,
    };
  }

  appendSequenceParts(
    mergedParts,
    leadingWhitespace,
    elementNode,
    trailingWhitespace,
    nextMark,
  );

  return {
    state: {
      ...state,
      mergedPairs: state.mergedPairs + 1,
      scanIndex: afterTrailing + 1,
    },
    shouldBreak: false,
  };
}

function scanMarkElementMarkSequence(
  originalChildren: ReactNode[],
  startIndex: number,
  blockAnnotations: Annotation[],
  annotation: Annotation,
  firstMark: MarkEl,
): {
  mergedParts: Children;
  mergedPairs: number;
  nextIndex: number;
  resumeIndex: number;
  stoppedOnUnpairedElement: boolean;
} {
  const mergedParts: Children = [];
  const pushMarkChildren = (mark: MarkEl) => {
    mergedParts.push(...toChildrenArray(mark));
  };
  pushMarkChildren(firstMark);

  let state: ScanState = {
    scanIndex: startIndex + 1,
    mergedPairs: 0,
    resumeIndex: startIndex + 1,
    stoppedOnUnpairedElement: false,
  };

  while (state.scanIndex < originalChildren.length) {
    const result = processScanMarkIteration(
      originalChildren,
      state,
      blockAnnotations,
      annotation,
      mergedParts,
    );
    state = result.state;
    if (result.shouldBreak) {
      break;
    }
  }

  return {
    mergedParts,
    mergedPairs: state.mergedPairs,
    stoppedOnUnpairedElement: state.stoppedOnUnpairedElement,
    resumeIndex: state.resumeIndex,
    nextIndex: state.scanIndex,
  };
}

const isWhitespaceNode = (child: ReactNode): boolean => {
  return typeof child === 'string' && child.trim() === '';
};

const isElementNode = (child: ReactNode): boolean => {
  return typeof child === 'object';
};

function countNonWhitespaceInParts(parts: Children): number {
  return getNonWhitespaceCount(parts.map((p) => extractText(p) || '').join(''));
}

function collectPartsAfterFirstMark(
  originalChildren: ReactNode[],
  firstMark: MarkEl,
  startIndex: number,
  targetNonWs: number,
): { collectedParts: Children; nextIndex: number } {
  /* Collect subsequent children after the first mark until the collected
  non‑whitespace characters reach the target non-whitespace offset. */
  const collectedParts: Children = [];
  collectedParts.push(...toChildrenArray(firstMark));

  let collectIndex = startIndex + 1;
  while (
    collectIndex < originalChildren.length &&
    countNonWhitespaceInParts(collectedParts) < targetNonWs
  ) {
    const part = originalChildren[collectIndex];
    if (typeof part === 'string') {
      collectedParts.push(part);
      collectIndex++;
      continue;
    }
    if (isElementChild(part)) {
      collectedParts.push(part as ReactElement);
      collectIndex++;
      continue;
    }
    if (isMarkNode(part)) {
      collectedParts.push(...toChildrenArray(part as MarkEl));
      collectIndex++;
      continue;
    }
    break;
  }

  return { collectedParts, nextIndex: collectIndex };
}

function appendTrailingWhitespaceAndAnchor(
  originalChildren: ReactNode[],
  startIndex: number,
  nodes: ReactNode[],
): { nodes: ReactNode[]; nextIndex: number } {
  let collectIndex = startIndex;

  // Add any trailing whitespace nodes to the altered children.
  while (
    collectIndex < originalChildren.length &&
    isWhitespaceNode(originalChildren[collectIndex])
  ) {
    nodes.push(originalChildren[collectIndex] as string);
    collectIndex++;
  }

  if (
    collectIndex < originalChildren.length &&
    isElementNode(originalChildren[collectIndex])
  ) {
    const nextEl = originalChildren[collectIndex] as ReactElement;
    /* If the next element is an anchor element, add it to the altered
    children as is. Anchor elements are interactive, so we don't want to
    split them because it might break interactivity and/or accessibility.*/
    if (typeof nextEl.type === 'string' && nextEl.type === 'a') {
      nodes.push(nextEl);
      collectIndex++;
    }
  }

  return { nodes, nextIndex: collectIndex };
}

function collectUntilTargetAndSplit(
  originalChildren: ReactNode[],
  firstMark: MarkEl,
  startIndex: number,
  annotation: Annotation,
  blockAnnotations: Annotation[],
  blockId: BlockIndex | undefined,
  childIndex: number,
) {
  // Compute the target non-whitespace offset for the annotation.
  const startOff = annotation.position.start.offset;
  const endOff = annotation.position.end.offset;
  const targetNonWs = Math.max(0, endOff - startOff);

  /* Collect subsequent children after the first mark until the collected
  non‑whitespace characters reach the target non-whitespace offset. */
  const { collectedParts, nextIndex: collectedNextIndex } =
    collectPartsAfterFirstMark(
      originalChildren,
      firstMark,
      startIndex,
      targetNonWs,
    );

  /* Split the collected parts into "keep" (content to wrap in a single Highlight)
   and "remainder" (trailing content that should not be highlighter). */
  const { keep, remainder } = splitChildren(
    collectedParts,
    annotation,
    blockAnnotations,
    blockId ?? undefined,
  );

  const nodes: ReactNode[] = [];

  nodes.push(
    createElement(
      Highlight,
      {
        key: `merged-fallback-${blockId}-${childIndex}`,
        annotation,
      },
      keep,
    ),
  );

  if (remainder.length > 0) {
    nodes.push(...remainder);
  }

  const { nodes: finalNodes, nextIndex } = appendTrailingWhitespaceAndAnchor(
    originalChildren,
    collectedNextIndex,
    nodes,
  );

  return { nodes: finalNodes, nextIndex };
}

/**
 * At the start of the function, there is multiple mark elements of the same
 * annotation. This function merges them into a single one when possible.
 */
export default function mergeHighlights(
  originalChildren: ReactNode[],
  blockId: BlockIndex | undefined,
  annotationsByBlock: BlockAnnotationsLookup,
): ReactNode[] {
  if (!blockId) {
    // No highlights to apply without blockId or annotations.
    return originalChildren;
  }
  if (annotationsByBlock.size === 0) {
    // No highlights to apply without blockId or annotations.
    return originalChildren;
  }

  const blockAnnotations = annotationsByBlock.get(blockId) || [];
  if (blockAnnotations.length === 0 || originalChildren.length <= 2) {
    /* With only 0-1 children, there is nothing to merge.
    If there are 2 children, it can only be two situations:
    - <mark> + <mark>
    - <mark> + <some-element> (=> no second mark to pair across the element).
    In both cases, no merging is needed.*/
    return originalChildren;
  }

  /** The children that have been altered to include highlights. */
  const alteredChildren: ReactNode[] = [];

  function processNonMarkNode(
    node: ReactNode,
    index: number,
  ): { nodes: ReactNode[]; nextIndex: number } {
    /* The node is not a mark node so we can just add it to the altered
    children and continue to the next child. */
    return { nodes: [node], nextIndex: index + 1 };
  }

  function processMarkNode(
    node: MarkEl,
    index: number,
    resolvedBlockId: BlockIndex,
  ): { nodes: ReactNode[]; nextIndex: number } {
    const firstMark = node;

    const annotation = getAnnotationForId(
      blockAnnotations,
      firstMark.props.annotation?.id,
    );

    if (
      !annotation ||
      !blockContainsEntireAnnotation(resolvedBlockId, annotation)
    ) {
      /* If there is no annotation to inject, or the annotation starts and ends
      inside the block, push the node in altered children as is. */
      return { nodes: [node], nextIndex: index + 1 };
    }

    const {
      mergedParts,
      mergedPairs,
      nextIndex,
      resumeIndex,
      stoppedOnUnpairedElement,
    } = scanMarkElementMarkSequence(
      originalChildren,
      index,
      blockAnnotations,
      annotation,
      firstMark,
    );

    /* In case no mark->element->mark sequence is found, we fallback to
     splitting the children at the non-whitespace target. */
    if (mergedPairs === 0) {
      const { nodes, nextIndex: fallbackNextIndex } =
        collectUntilTargetAndSplit(
          originalChildren,
          firstMark,
          index,
          annotation,
          blockAnnotations,
          resolvedBlockId,
          index,
        );
      return { nodes, nextIndex: fallbackNextIndex };
    }

    const { keep: trimmedAcc, remainder: trimmedTail } = splitChildren(
      mergedParts,
      annotation,
      blockAnnotations,
      blockId ?? undefined,
    );

    const mergedHighlight = createElement(
      Highlight,
      { key: `merged-${resolvedBlockId}-${index}`, annotation },
      trimmedAcc,
    );

    const mergedNodes: ReactNode[] = [mergedHighlight];
    if (trimmedTail.length > 0) {
      mergedNodes.push(...trimmedTail);
    }
    const newIndex = stoppedOnUnpairedElement ? resumeIndex : nextIndex;
    return { nodes: mergedNodes, nextIndex: newIndex };
  }

  let childIndex = 0;
  const ensuredBlockId = blockId;
  while (childIndex < originalChildren.length) {
    const node = originalChildren[childIndex];
    const { nodes, nextIndex } = isMarkNode(node)
      ? processMarkNode(node, childIndex, ensuredBlockId)
      : processNonMarkNode(node, childIndex);
    alteredChildren.push(...nodes);
    childIndex = nextIndex;
  }

  return alteredChildren.length > 0 ? alteredChildren : originalChildren;
}
